---
title: "Candidates in Chosen"
description: "Candidates land in a table you can also view as a kanban board. How to move them through your pipeline, parse resumes, score matches, and export data."
category: "candidates"
keywords:
  - "ATS candidates"
  - "manage candidates"
  - "candidate pipeline"
  - "kanban candidates"
schema: "HowTo"
nav:
  section: "guides"
  order: 10
---

Candidates land in a table. You can also view that table as a kanban board. You drag people between statuses, and Chosen handles the parsing, scoring, and exporting around the edges.

This is the section you'll live in once applications start arriving. Each page below covers one piece of it — start with the views and statuses if you're new, jump straight to the page you need if you're not.

## In this section

<Steps>
  <li>**[The table and the board](/guides/candidates/table-and-boards)** — the two ways to look at your candidates, and when each one helps.</li>
  <li>**[Statuses and your pipeline](/guides/candidates/statuses-and-pipeline)** — the stages candidates move through, and how to change the set to fit your process.</li>
  <li>**[Resume parsing](/guides/candidates/resume-parsing)** — what Chosen pulls out of an uploaded resume, and what it misses.</li>
  <li>**[Custom fields](/guides/candidates/custom-fields)** — add your own columns to the candidate record and let resume parsing fill them in.</li>
  <li>**[Match rating](/guides/candidates/match-rating)** — the AI match score, how it's built, and why it's a sorting aid rather than a verdict.</li>
  <li>**[Bulk import and export](/guides/candidates/bulk-import-and-export)** — get candidates in from a spreadsheet, get data back out as CSV or resumes.</li>
  <li>**[Duplicates and merging](/guides/candidates/duplicates-and-merging)** — how Chosen spots the same person twice, and how to merge when it doesn't.</li>
</Steps>

<Callout type="tip" title="Adding candidates is covered where you'll need it">
Most candidates arrive on their own through your [public careers page](/guides/careers). To add one yourself — a resume upload, a manual entry, a spreadsheet of names — see [bulk import and export](/guides/candidates/bulk-import-and-export).
</Callout>

When you want to go find people instead of waiting for them to apply, that's [sourcing and outreach](/guides/sourcing) — a different surface with its own section.
